import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { useOrientationChange } from './useOrientationChange'

const SCROLL_TOLERANCE = 2

export const useScrollableCheck = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollContentRef = useRef<HTMLDivElement>(null)

  const [isInitialScrollable, setInitialScrollable] = useState<boolean>(false)
  const [isScrollable, setScrollable] = useState<boolean>(false)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const [scrollHeight, setScrollHeight] = useState<number>(0)
  const [portraitHeight, setPortraitHeight] = useState<number>(0)
  const [landscapeHeight, setLandscapeHeight] = useState<number>(0)
  const [prevPortraitHeight, setPrevPortraitHeight] = useState<number>(0)
  const [prevLandscapeHeight, setPrevLandscapeHeight] = useState<number>(0)

  const isPortraitHeightChanged = () => {
    const newPortraitHeight = computePortraitHeight()
    const isHeightChanged = newPortraitHeight != prevPortraitHeight
    isHeightChanged && setPrevPortraitHeight(newPortraitHeight)
    return isHeightChanged
  }

  const isLandscapeHeightChanged = () => {
    const newLandscapeHeight = computeLandscapeHeight()
    const isHeightChanged = newLandscapeHeight != prevLandscapeHeight
    isHeightChanged && setPrevLandscapeHeight(newLandscapeHeight)
    return isHeightChanged
  }

  const checkScroll = useCallback(() => {
    const container = scrollContainerRef.current
    const content = scrollContentRef.current

    if (!container || !content) {
      return { canScrollUp: false, canScrollDown: false }
    }

    const scrollNumber = container.scrollTop
    const clientHeight = container.clientHeight
    const contentScrollHeight = content.scrollHeight
    const newPortraitHeight = computePortraitHeight()
    const newLandscapeHeight = computeLandscapeHeight()

    const canScrollUp = scrollNumber > 0
    const canScrollDown = scrollNumber + clientHeight < contentScrollHeight - SCROLL_TOLERANCE

    setCanScrollUp(canScrollUp)
    setCanScrollDown(canScrollDown)
    setScrollHeight(contentScrollHeight)
    setPortraitHeight(newPortraitHeight)
    setLandscapeHeight(newLandscapeHeight)

    return { canScrollUp, canScrollDown }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computePortraitHeight])

  const resetScrollable = useCallback(() => {
    const { canScrollUp, canScrollDown } = checkScroll()
    const isScrollable = canScrollUp || canScrollDown
    setScrollable(isScrollable)
    setInitialScrollable(isScrollable)
  }, [checkScroll])

  useLayoutEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return
    }

    const initialScrollHeight = scrollContentRef.current?.scrollHeight || 0
    setScrollHeight(initialScrollHeight)

    const { canScrollUp, canScrollDown } = checkScroll()
    setScrollable(canScrollUp || canScrollDown)
    setInitialScrollable(canScrollUp || canScrollDown)

    container.addEventListener('scroll', checkScroll)

    return () => {
      container.removeEventListener('scroll', checkScroll)
    }
  }, [checkScroll])

  const { isOrientationInProgress } = useOrientationChange(resetScrollable, { timeout: 1000 })

  return {
    isOrientationInProgress,
    isInitialScrollable,
    isScrollable: isScrollable || isInitialScrollable,
    canScrollUp,
    canScrollDown,
    scrollHeight,
    portraitHeight,
    landscapeHeight,
    scrollContainerRef,
    scrollContentRef,
    checkScroll,
    isPortraitHeightChanged,
    isLandscapeHeightChanged,
  }
}

export const computePortraitHeight = () => {
  if (typeof window === 'undefined') {
    return 0
  }
  return Math.max(window.innerWidth, window.innerHeight)
}

export const computeLandscapeHeight = () => {
  if (typeof window === 'undefined') {
    return 0
  }
  return Math.min(window.innerWidth, window.innerHeight)
}
